import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Calendar,
  Plus,
  Trash2,
  ChevronDown,
  Copy,
  Zap,
  FileText,
  Bell
} from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { useAuth } from '../../contexts/AuthContext';
import {
  SlipSubmission,
  ReviewedPick,
  REJECTION_REASONS,
  SUPPORTED_SPORTSBOOKS,
  BET_TYPES,
  SPORTS_LEAGUES
} from '../../types';
import {
  useApproveSlipSubmission,
  useRejectSlipSubmission
} from '../../hooks/useFirebaseData';
import { TestModeWarning } from '../common/TestModeBanner';
import { safeFormat as format } from '../../utils/safeFormat';

interface SlipReviewModalProps {
  submission: SlipSubmission;
  onClose: () => void;
}

// Empty pick template
const createEmptyPick = (): ReviewedPick => ({
  sport: '',
  league: '',
  event: '',
  selection: '',
  odds: '',
  gameTime: null
});

export const SlipReviewModal: React.FC<SlipReviewModalProps> = ({ submission, onClose }) => {
  const { user } = useAuth();

  // Form state
  const [sportsbook, setSportsbook] = useState(submission.aiExtraction?.sportsbook || '');
  const [betType, setBetType] = useState(submission.aiExtraction?.betType || '');
  const [totalOdds, setTotalOdds] = useState(submission.aiExtraction?.totalOdds || '');
  const [wagerAmount, setWagerAmount] = useState(submission.aiExtraction?.wagerAmount || '');
  const [potentialPayout, setPotentialPayout] = useState(submission.aiExtraction?.potentialPayout || '');
  const [picks, setPicks] = useState<ReviewedPick[]>(() => {
    if (submission.aiExtraction?.picks?.length) {
      return submission.aiExtraction.picks.map(p => ({
        sport: p.sport || '',
        league: p.league || '',
        event: p.event,
        selection: p.selection,
        odds: p.odds,
        gameTime: null
      }));
    }
    return [createEmptyPick()];
  });
  const [notes, setNotes] = useState('');

  // Rejection state
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [customRejectionReason, setCustomRejectionReason] = useState('');

  // Mutations
  const approveSubmission = useApproveSlipSubmission();
  const rejectSubmission = useRejectSlipSubmission();

  const isProcessing = approveSubmission.isPending || rejectSubmission.isPending;

  // Get available leagues for selected sport
  const getLeaguesForSport = (sport: string): readonly string[] => {
    return SPORTS_LEAGUES[sport as keyof typeof SPORTS_LEAGUES] || [];
  };

  // Pick management
  const addPick = () => {
    setPicks([...picks, createEmptyPick()]);
  };

  const removePick = (index: number) => {
    if (picks.length > 1) {
      setPicks(picks.filter((_, i) => i !== index));
    }
  };

  const updatePick = (index: number, field: keyof ReviewedPick, value: string | number | null) => {
    const updated = [...picks];
    updated[index] = { ...updated[index], [field]: value };

    // Reset league when sport changes
    if (field === 'sport') {
      updated[index].league = '';
    }

    setPicks(updated);
  };

  // Validation
  const isValidForApproval = useMemo(() => {
    if (!sportsbook || !betType) return false;
    return picks.every(p => p.event.trim() && p.selection.trim());
  }, [sportsbook, betType, picks]);

  // Handle approve
  const handleApprove = async () => {
    if (!user || !isValidForApproval) return;

    try {
      await approveSubmission.mutateAsync({
        submissionId: submission.submissionId,
        reviewedBy: user.uid,
        reviewedByUsername: user.username,
        sportsbook,
        betType,
        totalOdds,
        wagerAmount,
        potentialPayout,
        picks,
        notes
      });
      onClose();
    } catch (error) {
      console.error('Approval error:', error);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!user || !rejectionReason) return;

    const reason = rejectionReason === 'other' ? customRejectionReason : rejectionReason;

    try {
      await rejectSubmission.mutateAsync({
        submissionId: submission.submissionId,
        reviewedBy: user.uid,
        reviewedByUsername: user.username,
        reason,
        notes
      });
      onClose();
    } catch (error) {
      console.error('Rejection error:', error);
    }
  };

  // Copy AI text
  const copyAIText = () => {
    if (submission.aiExtraction?.rawText) {
      navigator.clipboard.writeText(submission.aiExtraction.rawText);
    }
  };

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-5xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <FileText size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Review Submission</h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                {submission.submissionId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Left: Image & Info */}
          <div className="lg:w-1/2 p-6 border-b lg:border-b-0 lg:border-r border-border-light dark:border-border-dark">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-4">
              {submission.userPhotoURL ? (
                <img
                  src={submission.userPhotoURL}
                  alt={submission.username}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <User size={20} className="text-primary" />
                </div>
              )}
              <div>
                <p className="font-medium">@{submission.username}</p>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark flex items-center gap-1">
                  <Calendar size={12} />
                  {format(submission.createdAt, 'PPp')}
                </p>
              </div>
              <span className="ml-auto px-2 py-0.5 bg-surface-light dark:bg-gray-800 rounded text-xs uppercase font-medium">
                {submission.source}
              </span>
            </div>

            {/* Processing Error Alert */}
            {(submission.processingError || submission.processingResult?.error) && (
              <div className="mb-4 p-4 bg-error/10 border border-error/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-error/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle size={20} className="text-error" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-error">
                      {submission.processingResult?.userMessage?.title || 'Processing Error'}
                    </h4>
                    <p className="mt-1 text-sm text-error/80">
                      {submission.processingResult?.userMessage?.message || 'An error occurred while processing this slip.'}
                    </p>
                    {submission.processingError && (
                      <details className="mt-2">
                        <summary className="text-xs text-error/60 cursor-pointer hover:text-error">
                          Technical Details
                        </summary>
                        <pre className="mt-2 p-2 bg-error/5 rounded text-xs text-error/70 whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">
                          {submission.processingError}
                          {submission.processingResult?.errorCode && `\nError Code: ${submission.processingResult.errorCode}`}
                        </pre>
                      </details>
                    )}
                    {submission.processingResult?.userMessage?.canRetry && (
                      <p className="mt-2 text-xs text-error/60 italic">
                        The user can retry uploading this slip.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Image */}
            <div className="bg-surface-light dark:bg-gray-900 rounded-lg overflow-hidden mb-4">
              <img
                src={submission.imageURL}
                alt="Slip"
                className="w-full max-h-[400px] object-contain"
              />
            </div>

            {/* AI Extraction Results */}
            {submission.aiExtraction && !submission.aiExtraction.error && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <Zap size={16} className="text-warning" />
                    AI Analysis
                  </h4>
                  {submission.aiExtraction.confidence !== undefined && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      submission.aiExtraction.confidence >= 70
                        ? 'bg-success/10 text-success'
                        : submission.aiExtraction.confidence >= 40
                        ? 'bg-warning/10 text-warning'
                        : 'bg-error/10 text-error'
                    }`}>
                      {submission.aiExtraction.confidence}% confidence
                    </span>
                  )}
                </div>

                {/* Flags */}
                {submission.aiExtraction.flags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {submission.aiExtraction.flags.map((flag, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs flex items-center gap-1"
                      >
                        <AlertTriangle size={12} />
                        {flag.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}

                {/* Raw Text */}
                {submission.aiExtraction.rawText && (
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark">
                        OCR Text
                      </span>
                      <button
                        onClick={copyAIText}
                        className="text-xs text-primary flex items-center gap-1 hover:underline"
                      >
                        <Copy size={12} />
                        Copy
                      </button>
                    </div>
                    <pre className="text-xs whitespace-pre-wrap max-h-32 overflow-y-auto text-text-secondary-light dark:text-text-secondary-dark">
                      {submission.aiExtraction.rawText}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Duplicate Warning */}
            {submission.duplicateCheck?.isDuplicate && (
              <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-orange-500 font-medium mb-2">
                  <AlertTriangle size={16} />
                  Potential Duplicate Detected
                </div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  {submission.duplicateCheck.hashMatches?.length} exact match(es) found.
                  {submission.duplicateCheck.similarSubmissions?.length > 0 &&
                    ` ${submission.duplicateCheck.similarSubmissions.length} similar submission(s).`}
                </p>
              </div>
            )}
          </div>

          {/* Right: Form */}
          <div className="lg:w-1/2 p-6 max-h-[80vh] overflow-y-auto">
            <TestModeWarning action="Reviewing this submission" />

            <AnimatePresence mode="wait">
              {!showRejectForm ? (
                <motion.div
                  key="approve-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h4 className="font-bold">Slip Details</h4>

                  {/* Sportsbook & Bet Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Sportsbook *</label>
                      <select
                        value={sportsbook}
                        onChange={(e) => setSportsbook(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        {SUPPORTED_SPORTSBOOKS.map((book) => (
                          <option key={book} value={book}>{book}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bet Type *</label>
                      <select
                        value={betType}
                        onChange={(e) => setBetType(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        {BET_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Odds & Amounts */}
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Total Odds"
                      placeholder="+450"
                      value={totalOdds}
                      onChange={(e) => setTotalOdds(e.target.value)}
                    />
                    <Input
                      label="Wager"
                      placeholder="$25"
                      value={wagerAmount}
                      onChange={(e) => setWagerAmount(e.target.value)}
                    />
                    <Input
                      label="Payout"
                      placeholder="$137.50"
                      value={potentialPayout}
                      onChange={(e) => setPotentialPayout(e.target.value)}
                    />
                  </div>

                  {/* Picks */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium">Picks *</label>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Plus size={14} />}
                        onClick={addPick}
                      >
                        Add Pick
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {picks.map((pick, index) => (
                        <div
                          key={index}
                          className="p-3 bg-surface-light dark:bg-gray-800 rounded-lg space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark">
                              Pick #{index + 1}
                            </span>
                            {picks.length > 1 && (
                              <button
                                onClick={() => removePick(index)}
                                className="p-1 text-error hover:bg-error/10 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={pick.sport}
                              onChange={(e) => updatePick(index, 'sport', e.target.value)}
                              className="px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-border-light dark:border-border-dark rounded focus:ring-2 focus:ring-primary"
                            >
                              <option value="">Sport</option>
                              {Object.keys(SPORTS_LEAGUES).map((sport) => (
                                <option key={sport} value={sport}>{sport}</option>
                              ))}
                            </select>
                            <select
                              value={pick.league}
                              onChange={(e) => updatePick(index, 'league', e.target.value)}
                              className="px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-border-light dark:border-border-dark rounded focus:ring-2 focus:ring-primary"
                              disabled={!pick.sport}
                            >
                              <option value="">League</option>
                              {getLeaguesForSport(pick.sport).map((league) => (
                                <option key={league} value={league}>{league}</option>
                              ))}
                            </select>
                          </div>

                          <input
                            type="text"
                            placeholder="Event (e.g., Lakers vs Warriors)"
                            value={pick.event}
                            onChange={(e) => updatePick(index, 'event', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-border-light dark:border-border-dark rounded focus:ring-2 focus:ring-primary"
                          />

                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="Selection"
                              value={pick.selection}
                              onChange={(e) => updatePick(index, 'selection', e.target.value)}
                              className="col-span-2 px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-border-light dark:border-border-dark rounded focus:ring-2 focus:ring-primary"
                            />
                            <input
                              type="text"
                              placeholder="Odds"
                              value={pick.odds}
                              onChange={(e) => updatePick(index, 'odds', e.target.value)}
                              className="px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-border-light dark:border-border-dark rounded focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Notes</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional notes about this slip..."
                      rows={2}
                      className="w-full px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Notification Info */}
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-center gap-2 text-primary text-sm">
                      <Bell size={16} />
                      <span className="font-medium">User will be notified</span>
                    </div>
                    <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      A push notification will be sent to the user when you approve or reject this submission.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-border-light dark:border-border-dark">
                    <Button
                      variant="danger"
                      onClick={() => setShowRejectForm(true)}
                      icon={<XCircle size={18} />}
                      className="flex-1"
                    >
                      Reject
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleApprove}
                      disabled={!isValidForApproval || isProcessing}
                      loading={approveSubmission.isPending}
                      icon={<CheckCircle size={18} />}
                      className="flex-1"
                    >
                      Approve & Notify
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="reject-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowRejectForm(false)}
                      className="p-1 hover:bg-surface-light dark:hover:bg-gray-800 rounded"
                    >
                      <ChevronDown size={20} className="rotate-90" />
                    </button>
                    <h4 className="font-bold text-error">Reject Submission</h4>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Rejection Reason *</label>
                    <div className="space-y-2">
                      {REJECTION_REASONS.map((reason) => (
                        <label
                          key={reason.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            rejectionReason === reason.id
                              ? 'border-error bg-error/5'
                              : 'border-border-light dark:border-border-dark hover:bg-surface-light dark:hover:bg-gray-800'
                          }`}
                        >
                          <input
                            type="radio"
                            name="rejection-reason"
                            value={reason.id}
                            checked={rejectionReason === reason.id}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="text-error focus:ring-error"
                          />
                          <span className="text-sm">{reason.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {rejectionReason === 'other' && (
                    <Input
                      label="Custom Reason"
                      placeholder="Enter rejection reason..."
                      value={customRejectionReason}
                      onChange={(e) => setCustomRejectionReason(e.target.value)}
                    />
                  )}

                  <div>
                    <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional notes..."
                      rows={2}
                      className="w-full px-3 py-2 bg-surface-light dark:bg-gray-800 border border-border-light dark:border-border-dark rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Notification Info */}
                  <div className="p-3 bg-error/5 border border-error/20 rounded-lg">
                    <div className="flex items-center gap-2 text-error text-sm">
                      <Bell size={16} />
                      <span className="font-medium">User will be notified</span>
                    </div>
                    <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      The user will receive a push notification with your rejection reason.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-border-light dark:border-border-dark">
                    <Button
                      variant="secondary"
                      onClick={() => setShowRejectForm(false)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleReject}
                      disabled={!rejectionReason || (rejectionReason === 'other' && !customRejectionReason.trim()) || isProcessing}
                      loading={rejectSubmission.isPending}
                      icon={<XCircle size={18} />}
                      className="flex-1"
                    >
                      Reject & Notify
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};
